import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/Button';
import ReportModal from 'components/moderation/ReportModal';
import { ReportContentType } from 'api/types/moderation';
import { moderationService } from 'api/services/ModerationService';
import { sellerService } from 'api/services/SellerService';
import { useAuth } from 'contexts/AuthContext';
import { notifyUserUnblocked } from 'utils/moderationBlockEvents';

interface BlockedUserRow {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
}

const PrivacySettingsSection: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [blockedExpanded, setBlockedExpanded] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<BlockedUserRow[]>([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [blockedError, setBlockedError] = useState<string | null>(null);
  const [reportUserId, setReportUserId] = useState<string | null>(null);

  const loadBlockedUsers = useCallback(async () => {
    setLoadingBlocked(true);
    setBlockedError(null);
    try {
      const res = await moderationService.getBlockedUserIds();
      const ids = res.isSuccess && res.data?.length ? res.data.map(String) : [];
      if (ids.length === 0) {
        setBlockedUsers([]);
        return;
      }
      const rows = await Promise.all(
        ids.map(async (userId) => {
          try {
            const profileRes = await sellerService.getProfile(userId);
            return {
              userId,
              displayName: profileRes.data?.displayName?.trim() || 'Kullanıcı',
              avatarUrl: profileRes.data?.avatarUrl ?? null,
            };
          } catch {
            return { userId, displayName: 'Kullanıcı', avatarUrl: null };
          }
        }),
      );
      setBlockedUsers(rows);
    } catch {
      setBlockedError('Engellenmiş kullanıcılar yüklenemedi.');
      setBlockedUsers([]);
    } finally {
      setLoadingBlocked(false);
    }
  }, []);

  useEffect(() => {
    if (blockedExpanded) {
      void loadBlockedUsers();
    }
  }, [blockedExpanded, loadBlockedUsers]);

  const handleUnblock = async (userId: string, displayName: string) => {
    if (!window.confirm(`${displayName} kullanıcısının engelini kaldırmak istediğinize emin misiniz?`)) {
      return;
    }
    const res = await moderationService.unblockUser(userId);
    if (res.isSuccess) {
      setBlockedUsers((prev) => prev.filter((u) => u.userId !== userId));
      notifyUserUnblocked(userId);
    } else {
      window.alert(res.message ?? 'Engel kaldırılamadı.');
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="card-title mb-4">Gizlilik Ayarları</h5>

        <div className="border rounded">
          <button
            type="button"
            className="btn btn-link text-decoration-none text-body w-100 d-flex align-items-center justify-content-between px-3 py-3"
            onClick={() => setBlockedExpanded((v) => !v)}
            aria-expanded={blockedExpanded}
          >
            <span className="fw-semibold">Engellenmiş Kullanıcılar</span>
            <i className={`fas fa-chevron-${blockedExpanded ? 'up' : 'down'} text-muted`} aria-hidden />
          </button>

          {blockedExpanded && (
            <div className="border-top px-3 pb-3">
              {loadingBlocked ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : blockedError ? (
                <p className="text-danger small mb-0 py-3">{blockedError}</p>
              ) : blockedUsers.length === 0 ? (
                <p className="text-muted small mb-0 py-3">Engellediğiniz kullanıcı bulunmuyor.</p>
              ) : (
                <ul className="list-group list-group-flush">
                  {blockedUsers.map((row) => (
                    <li
                      key={row.userId}
                      className="list-group-item px-0 d-flex align-items-center gap-3 bg-transparent"
                    >
                      {row.avatarUrl ? (
                        <img
                          src={row.avatarUrl}
                          alt=""
                          className="rounded-circle flex-shrink-0"
                          width={44}
                          height={44}
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 44, height: 44 }}
                        >
                          <i className="fas fa-user" aria-hidden />
                        </div>
                      )}
                      <span className="flex-grow-1 text-truncate">{row.displayName}</span>
                      <div className="dropdown flex-shrink-0">
                        <Button
                          className="btn btn-light btn-sm rounded-circle"
                          type="button"
                          id={`blocked-user-menu-${row.userId}`}
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          title="Menü"
                        >
                          <i className="fas fa-ellipsis-v" />
                        </Button>
                        <ul
                          className="dropdown-menu dropdown-menu-end"
                          aria-labelledby={`blocked-user-menu-${row.userId}`}
                        >
                          <li>
                            <button
                              type="button"
                              className="dropdown-item py-2"
                              onClick={() => setReportUserId(row.userId)}
                            >
                              <i className="fas fa-flag me-2" />
                              Şikayet Et
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="dropdown-item py-2"
                              onClick={() => void handleUnblock(row.userId, row.displayName)}
                            >
                              <i className="fas fa-unlock me-2" />
                              Engeli Kaldır
                            </button>
                          </li>
                        </ul>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {reportUserId && (
        <ReportModal
          modalId="profileBlockedUserReportModal"
          contentType={ReportContentType.Profile}
          reportedUserId={reportUserId}
          isAuthenticated={isAuthenticated}
          onRequireLogin={() => navigate('/giris')}
          open
          onClose={() => setReportUserId(null)}
        />
      )}
    </div>
  );
};

export default PrivacySettingsSection;
